// MUD's numerical formatting contract, which is binding rather than advisory
// (mud/10-data-formatting.md: "This is a binding formatting contract, not
// styling advice. Every number and date SIMSM renders" follows it).
//
// Two rules, and they are the two every locale-aware formatter gets wrong here:
//
//   > Use a **space** as the thousands separator to improve readability in large
//   > numbers. **Avoid using commas or periods** as thousands separators, as they
//   > may be confused with decimal points in some contexts.
//
//   > Use a **dot** as the decimal point to align with local conventions.
//
// So `1 000.50` — never `1,000.50` and never `1.000,50`.
//
// This exists because `Intl.NumberFormat('ro-MD')` produces exactly the form the
// contract forbids: Romanian groups thousands with a period and marks decimals
// with a comma, so `1234.5` came out as `1.234,5` — the same glyph MUD warns is
// read as a decimal point. Asking for `en-US` instead would only trade it for
// the comma the first rule names. Neither locale implements this contract, so it
// is spelled out here rather than delegated.
//
// Grouping uses U+00A0 NO-BREAK SPACE, not U+0020: a plain space lets a browser
// break `12 500` across two lines mid-number, and half a quantity at the end of
// a table cell is worse than an unbroken one. It is a space to the reader and to
// the contract; it is only unbreakable to the line-breaker.
//
// Lives beside `date-format.ts` because it answers the other half of the same
// document, and for the same reason: a formatting contract belongs in one place,
// or the next register picks its own separator.

const NBSP = ' ';

/** Everything from `-` to the grouped integer part, per the contract above. */
function group(digits: string): string {
  // Right to left in threes: `1234567` → `1 234 567`.
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

export interface FormatNumberOptions {
  /**
   * Most decimals to show. The contract lets whole numbers drop them entirely
   * ("For whole numbers, decimal places can be omitted to reduce visual
   * clutter"), so a trailing `.0` is always trimmed — `maxDecimals` is a
   * ceiling, never a pad.
   */
  maxDecimals?: number;
}

/**
 * A number as MUD says to write it: space-grouped thousands, dot decimal, no
 * trailing zeros.
 *
 * Non-finite input (NaN, ±Infinity) has no correct rendering, so it comes back
 * empty and the caller draws its own empty cell rather than printing "NaN" into
 * a register.
 */
export function formatNumber(value: number, options: FormatNumberOptions = {}): string {
  if (!Number.isFinite(value)) return '';
  const maxDecimals = options.maxDecimals ?? 2;
  // toFixed rounds half away from zero and gives a plain decimal string with no
  // exponent for everything a stock quantity or a row count can be.
  const fixed = Math.abs(value).toFixed(maxDecimals);
  const [whole = '0', fraction = ''] = fixed.split('.');
  const trimmed = fraction.replace(/0+$/, '');
  const sign = value < 0 ? '-' : '';
  return trimmed ? `${sign}${group(whole)}.${trimmed}` : `${sign}${group(whole)}`;
}

/**
 * A percentage: the symbol **after** the number **with no space**, at most one
 * decimal, and no decimals at all when the value is whole — all three verbatim
 * from the contract's own percentage section.
 */
export function formatPercent(value: number): string {
  const formatted = formatNumber(value, { maxDecimals: 1 });
  return formatted ? `${formatted}%` : '';
}
